import React, { useCallback, useEffect, useState } from "react";
import { Button, Card, Space, Typography, Checkbox, Select } from "antd";
import { Test, TestAttempt, TestQuestion } from "../../stores/TestsStore";
import { useTranslation } from "react-i18next";
import MonacoEditor from "@monaco-editor/react";
import { useNavigate } from "react-router-dom";
import { useStore } from "../../stores/root-store-context";
import { TypeTestQuestion } from "../../enums/TypeTestQuestion";

const { Text } = Typography;
const { Option } = Select;

interface StepTwoProps {
    testGroupQuestions: { [key: number]: TestQuestion[] };
    addNewQuestion: (newQuestion: TestQuestion) => void;
    currentTestAttempt: TestAttempt;
    maxTimeMinutes: number;
    language: string;
    onFinish: (answers: Array<{ questionId: number; answer: string | number | number[] }>, minutesSpent: number) => void;
}

const TestStepTwo: React.FC<StepTwoProps> = ({ addNewQuestion, maxTimeMinutes, testGroupQuestions, onFinish, language, currentTestAttempt }) => {
    const { t } = useTranslation();
    const [currentQuestionIndex, setCurrentQuestionIndex] = useState<number>(0);
    const [answers, setAnswers] = useState<Array<{ questionId: number; answer: string | number | number[] }>>([]);
    const [timeLeft, setTimeLeft] = useState<number>(0);
    const [totalTimeSpent, setTotalTimeSpent] = useState<number>(0);
    const [currentLanguage, setCurrentLanguage] = useState<string>(language === "any" ? "en" : language);
    const navigate = useNavigate();
    const { applicationStore, testsStore } = useStore();

    const filteredQuestions = Object.values(testGroupQuestions).map(group =>
        group.find(q => q.language === currentLanguage) || group[0]
    );

    const testQuestions = Array.from(new Set(filteredQuestions.filter(q => q)));

    useEffect(() => {
        const calculateTime = () => {
            const singleChoiceTime = 30;
            const multiChoiceTime = 45;
            const otherQuestions = testQuestions.filter(q => q.typeTestQuestionId !== TypeTestQuestion.SingleChoice && q.typeTestQuestionId !== TypeTestQuestion.MultipleChoice);
            const remainingTime = maxTimeMinutes * 60 - (testQuestions.length - otherQuestions.length) * (singleChoiceTime + multiChoiceTime);
            const otherTimePerQuestion = otherQuestions.length > 0 ? Math.floor(remainingTime / otherQuestions.length) : 0;
            return testQuestions.map(q => {
                if (q.typeTestQuestionId === TypeTestQuestion.SingleChoice) return singleChoiceTime;
                if (q.typeTestQuestionId === TypeTestQuestion.MultipleChoice) return multiChoiceTime;
                return otherTimePerQuestion;
            });
        };

        const times = calculateTime();
        setTimeLeft(times[currentQuestionIndex]);
    }, [maxTimeMinutes, currentQuestionIndex]);

    useEffect(() => {
        const timer = setInterval(() => {
            setTotalTimeSpent(prev => prev + 1);
        }, 1000);

        return () => clearInterval(timer);
    }, []);

    useEffect(() => {
        if (timeLeft <= 0) return;
        const timer = setInterval(() => {
            setTimeLeft(prev => {
                if (prev <= 1) {
                    clearInterval(timer);
                    nextQuestion(currentQuestion);
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);

        return () => clearInterval(timer);
    }, [timeLeft]);

    const checkFullScreen = useCallback(() => {
        if (!document.fullscreenElement) {
            alert(t("passage_test.warning_full_screen"));
            navigate(`/${applicationStore.company.Tenant}/tests`);
            fetchEmptyResultGradeToServer();
        }
    }, [navigate, t]);

    const handleVisibilityChange = useCallback(() => {
        if (document.hidden) {
            alert(t("passage_test.warning_exit_site"));
            navigate(`/${applicationStore.company.Tenant}/tests`);
            fetchEmptyResultGradeToServer();
        }
    }, [navigate, t]);

    const fetchEmptyResultGradeToServer = async () => {
        const minutesSpent = Math.round(totalTimeSpent / 60);

        let testAttemptData: TestAttempt = {
            ...currentTestAttempt,
            finishDate: new Date().toISOString(),
            percentage: 0,
            timeSpentMinutes: minutesSpent || 1,
            testAnswers: [],
        }

        if (testAttemptData != null) {
            await testsStore.addTestAttempt(testAttemptData);
        }
    }

    const nextQuestion = (question: TestQuestion) => {
        if (currentQuestionIndex < testQuestions.length - 1) {
            setCurrentQuestionIndex(prev => prev + 1);
        } else {
            handleFinish();
        }
    };

    const handleFinish = () => {
        const minutesSpent = Math.round(totalTimeSpent / 60);
        handleCancelCheckFullScreen();
        onFinish(answers, minutesSpent);
    };

    const handleCancelCheckFullScreen = useCallback(() => {
        document.removeEventListener("fullscreenchange", checkFullScreen);
        document.removeEventListener("visibilitychange", handleVisibilityChange);
    }, [checkFullScreen, handleVisibilityChange]);


    useEffect(() => {
        document.addEventListener("fullscreenchange", checkFullScreen);
        document.addEventListener("visibilitychange", handleVisibilityChange);

        return () => {
            handleCancelCheckFullScreen();
        };
    }, [checkFullScreen, handleVisibilityChange, handleCancelCheckFullScreen]);

    const handleAnswerChange = (questionId: number, newAnswer: string | number | number[]) => {
        setAnswers(prev => {
            const existingAnswer = prev.find(a => a.questionId === questionId);
            if (existingAnswer) {
                return prev.map(a => (a.questionId === questionId ? { ...a, answer: newAnswer } : a));
            } else {
                return [...prev, { questionId, answer: newAnswer }];
            }
        });
    };

    const currentQuestion = testQuestions[currentQuestionIndex];

    const handleLanguageChange = (value: string) => {
        setCurrentLanguage(value);
    };

    useEffect(() => {
        testQuestions.forEach((q) => addNewQuestion(q));
    }, []);

    return (
        <Card style={{ maxWidth: '60%', margin: "0 auto", textAlign: "left", padding: "20px 15px" }}>
            <Space direction="vertical" style={{ width: "100%" }}>
                {language === "any" && applicationStore.siteConfig.isShowLanguage ? (
                    <div style={{ textAlign: "right", marginBottom: "20px" }}>
                        <Select
                            value={currentLanguage}
                            style={{ width: 120 }}
                            onChange={handleLanguageChange}
                        >
                            <Option value="en">{t("languages.en")}</Option>
                            <Option value="ru">{t("languages.ru")}</Option>
                            <Option value="ua">{t("languages.ua")}</Option>
                        </Select>
                    </div>
                ) : (null)}

                <div style={{ textAlign: "center" }}>
                    <Text strong style={{ fontSize: "16px", color: "#555" }}>
                        {t("passage_test.time_remaining")}: {new Date(timeLeft * 1000).toISOString().substr(14, 5)}
                    </Text>
                </div>

                <div key={currentQuestion.id} style={{ marginBottom: "20px" }}>
                    <Text style={{ fontWeight: 500, fontSize: "16px", color: "#333" }}>
                        {currentQuestion.name}
                    </Text>
                    <div style={{ marginTop: "20px" }}></div>
                    <Space direction="vertical" style={{ width: "100%" }}>
                        {(() => {
                            const currentAnswer = answers.find((a) => a.questionId === currentQuestion.id)?.answer;

                            switch (currentQuestion.typeTestQuestionId) {
                                case TypeTestQuestion.SingleChoice:
                                    return currentQuestion.testOptions.map((option) => (
                                        <Button
                                            key={option.id}
                                            onClick={() =>
                                                setAnswers((prev) =>
                                                    prev.some((a) => a.questionId === currentQuestion.id)
                                                        ? prev.map((a) =>
                                                            a.questionId === currentQuestion.id
                                                                ? { ...a, answer: option.id }
                                                                : a
                                                        )
                                                        : [...prev, { questionId: currentQuestion.id, answer: option.id }]
                                                )
                                            }
                                            type={currentAnswer === option.id ? "primary" : "default"}
                                            size="middle"
                                            style={{
                                                fontSize: "14px",
                                                width: "40%",
                                                height: "auto",
                                                whiteSpace: "normal",
                                                wordWrap: "break-word",
                                                wordBreak: "break-word",
                                                textAlign: "left",
                                            }}
                                        >
                                            {option.name}
                                        </Button>
                                    ));
                                case TypeTestQuestion.MultipleChoice:
                                    return currentQuestion.testOptions.map((option) => (
                                        <Checkbox
                                            key={option.id}
                                            checked={(currentAnswer as number[] | undefined)?.includes(option.id)}
                                            onChange={() =>
                                                setAnswers((prev) =>
                                                    prev.some((a) => a.questionId === currentQuestion.id)
                                                        ? prev.map((a) =>
                                                            a.questionId === currentQuestion.id
                                                                ? {
                                                                    ...a,
                                                                    answer: (a.answer as number[]).includes(option.id)
                                                                        ? (a.answer as number[]).filter((id) => id !== option.id)
                                                                        : [...(a.answer as number[]), option.id],
                                                                }
                                                                : a
                                                        )
                                                        : [
                                                            ...prev,
                                                            { questionId: currentQuestion.id, answer: [option.id] },
                                                        ]
                                                )
                                            }
                                            style={{ fontSize: "14px", color: "#333" }}
                                        >
                                            {option.name}
                                        </Checkbox>
                                    ));
                                case TypeTestQuestion.WriteCode:
                                case TypeTestQuestion.FixCode:
                                    return (
                                        <div style={{ border: '2px solid #ccc', borderRadius: '8px' }}>
                                            <MonacoEditor
                                                height={currentQuestion.typeTestQuestionId === TypeTestQuestion.FixCode ? "200px" : "300px"}
                                                theme="vs-light"
                                                value={typeof currentAnswer === "string" ? currentAnswer : currentQuestion.code || ""}
                                                language="plaintext"
                                                onChange={(value) =>
                                                    handleAnswerChange(currentQuestion.id, value || "")
                                                }
                                            />
                                        </div>
                                    );
                                case TypeTestQuestion.OpenAnswer:
                                    return (
                                        <textarea
                                            style={{
                                                width: "100%",
                                                height: currentQuestion.typeTestQuestionId === TypeTestQuestion.OpenAnswer ? "100px" : "150px",
                                                fontSize: "14px",
                                            }}
                                            value={typeof currentAnswer === "string" ? currentAnswer : ""}
                                            onChange={(e) =>
                                                handleAnswerChange(currentQuestion.id, e.target.value)
                                            }
                                            placeholder={t("Enter your answer here...")}
                                        />
                                    );
                                default:
                                    return <Text style={{ color: "red" }}>{t("Unknown question type")}</Text>;
                            }
                        })()}
                    </Space>
                </div>

                <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "20px" }}>
                    <Button
                        onClick={() => nextQuestion(currentQuestion)}
                        disabled={currentQuestionIndex === testQuestions.length - 1}
                    >
                        {t("passage_test.next")}
                    </Button>
                </div>

                {currentQuestionIndex === testQuestions.length - 1 && (
                    <div style={{ textAlign: "center" }}>
                        <Button
                            type="primary"
                            size="middle"
                            style={{ marginTop: "20px", fontSize: "18px", width: "auto" }}
                            onClick={handleFinish}
                        >
                            {t("passage_test.finish")}
                        </Button>
                    </div>
                )}
            </Space>
        </Card>
    );
};

export default TestStepTwo;